/**
 * Connection gate — prevents child components from mounting until
 * the Shute WebSocket is connected and the app signals readiness.
 *
 * Without this, components mount immediately and fire API calls
 * before auth tokens are validated or the WebSocket is established,
 * causing 401 cascades and error noise.
 *
 * Usage:
 *   <ConnectionGate ready={sessionReady}>
 *     <Routes>...</Routes>
 *     <LoginDialog />
 *   </ConnectionGate>
 */
import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { selectConnected, selectOffline, selectSessionID } from '../redux/shuteSlice';
import { useAppSelector } from '../../services/redux/hooks';

export function ConnectionGate({ children, ready = true, message }: {
  children: React.ReactNode;
  ready?: boolean;
  message?: string;
}) {
  const isConnected = useAppSelector(selectConnected);
  const isOffline = useAppSelector(selectOffline);
  const sessionID = useAppSelector(selectSessionID);

  if (!isConnected || !sessionID || !ready) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" sx={{ height: '70vh' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {message || 'Connecting'}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {isOffline ? 'Service is unreachable' : 'Establishing connection...'}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
}

export default ConnectionGate;
